import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Box, Button, IconButton, Snackbar, Switch, Typography } from "@mui/material";
import { EditOutlined } from "@mui/icons-material";
import dayjs from "dayjs";
import { useAuth } from "../../hooks/AuthProvider";
import AppTable from "../../common/app-table";
import ButtonOne from "../../common/normal-button";
import TableSearchInput from "../TableSearchInput";
import typography from "../../cssUtils/typography";
import { ColorsConstants, grayFontColor } from "../../cssUtils/constants";
import { EMI_REMINDERS_ENDPOINT, EMI_REMINDERS_EXPORT_ENDPOINT } from "../../config/urls";
import { formatInTenantTz } from "../../utils/dateUtils";
import ReminderProgress, { ReminderProgressLegend } from "./ReminderProgress";
import EMIRecordFormDialog, { ACTIVE_SWITCH_SX } from "./EMIRecordFormDialog";

const ROWS_PER_PAGE_OPTIONS = [20, 50, 100];

const FILTER_LABEL_SX = {
  ...typography.bodySmall,
  color: ColorsConstants.grayFontColor,
  fontWeight: 600,
  mb: 0.5,
  display: "block",
};

const ordinal = (n) => {
  const rem100 = n % 100;
  if (rem100 >= 11 && rem100 <= 13) return `${n}th`;
  return `${n}${{ 1: "st", 2: "nd", 3: "rd" }[n % 10] || "th"}`;
};

const formatPhone = (cc, phone) => {
  const national = phone?.length === 10 ? `${phone.slice(0, 5)} ${phone.slice(5)}` : phone;
  return `+${cc} ${national}`;
};

const formatAmount = (value) =>
  value == null || value === ""
    ? "-"
    : `₹${Number(value).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const formatDay = (iso) => (iso ? dayjs(iso).format("D MMM") : "-");

const DueDayFilter = ({ value, options, onChange }) => (
  <Box sx={{ display: "inline-flex", gap: 0.5, p: 0.5, bgcolor: "#F3F5F7", borderRadius: 2, flexWrap: "wrap" }}>
    {[{ value: "", label: "All" }, ...options.map((d) => ({ value: String(d), label: ordinal(d) }))].map((opt) => {
      const selected = value === opt.value;
      return (
        <Button
          key={opt.value || "all"}
          onClick={() => onChange(opt.value)}
          disableElevation
          sx={{
            ...typography.bodySmall,
            minWidth: 48,
            px: 1.5,
            py: 0.75,
            textTransform: "none",
            borderRadius: 1.5,
            color: selected ? ColorsConstants.primaryBackgroundColor : "#6B7280",
            fontWeight: selected ? 600 : 400,
            bgcolor: selected ? "#FFFFFF" : "transparent",
            boxShadow: selected ? "0 1px 2px rgba(16,24,40,0.08)" : "none",
            "&:hover": { bgcolor: selected ? "#FFFFFF" : "#E9EDF2" },
          }}
        >
          {opt.label}
        </Button>
      );
    })}
  </Box>
);

/**
 * Records tab: paginated EMI customers for the selected month's cycle, with
 * search (name / loan A/C / phone), Due date filter, CSV export, reminder
 * progress, Active toggle and Edit Customer.
 */
const EMIRecordsTab = ({ month }) => {
  const { getValidToken } = useAuth();

  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [dueDays, setDueDays] = useState([]);
  const [scheduleLabels, setScheduleLabels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [controller, setController] = useState({ page: 0, rowsPerPage: 20 });

  const [searchQuery, setSearchQuery] = useState("");
  const [dueDay, setDueDay] = useState("");

  const [editRecord, setEditRecord] = useState(null);
  const [saving, setSaving] = useState(false);
  const [togglingIds, setTogglingIds] = useState(() => new Set());
  const [exporting, setExporting] = useState(false);
  const [toast, setToast] = useState(null);

  // Ignore responses from superseded requests (fast typing / paging).
  const latestRequest = useRef(0);

  const buildHeaders = useCallback(async (json = false) => {
    const token = await getValidToken();
    return {
      Authorization: `Bearer ${token}`,
      "X-Tenant-Id": localStorage.getItem("tenant"),
      ...(json ? { "Content-Type": "application/json" } : {}),
    };
  }, [getValidToken]);

  const filterParams = useCallback(() => {
    const params = new URLSearchParams({ month });
    if (searchQuery.trim()) params.set("search", searchQuery.trim());
    if (dueDay) params.set("due_day", dueDay);
    return params;
  }, [month, searchQuery, dueDay]);

  const fetchRows = useCallback(async () => {
    const requestId = ++latestRequest.current;
    setLoading(true);
    try {
      const params = filterParams();
      params.set("limit", controller.rowsPerPage);
      params.set("offset", controller.page * controller.rowsPerPage);
      const response = await fetch(`${EMI_REMINDERS_ENDPOINT}?${params}`, { headers: await buildHeaders() });
      if (!response.ok) throw new Error("Failed to fetch EMI records");
      const data = await response.json();
      if (requestId !== latestRequest.current) return;
      setRows(data.results ?? []);
      setRowCount(data.count ?? 0);
      setDueDays(data.due_days ?? []);
      setScheduleLabels((data.schedule ?? []).map((s) => s.label));
    } catch (error) {
      console.error("Error fetching EMI records:", error);
      if (requestId === latestRequest.current) {
        setToast({ severity: "error", message: "Could not load EMI records. Please try again." });
      }
    } finally {
      if (requestId === latestRequest.current) setLoading(false);
    }
  }, [buildHeaders, controller, filterParams]);

  useEffect(() => {
    const timeout = setTimeout(fetchRows, 350);
    return () => clearTimeout(timeout);
  }, [fetchRows]);

  // Any change to what's being listed starts again from the first page.
  const resetPage = () => setController((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
  useEffect(() => {
    setController((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
  }, [month]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    resetPage();
  };

  const handleDueDayChange = (value) => {
    setDueDay(value);
    resetPage();
  };

  const replaceRow = (updated) => setRows((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));

  const patchRecord = async (id, body) => {
    const response = await fetch(`${EMI_REMINDERS_ENDPOINT}${id}/?month=${month}`, {
      method: "PATCH",
      headers: await buildHeaders(true),
      body: JSON.stringify(body),
    });
    const data = await response.json().catch(() => ({}));
    return { ok: response.ok, data };
  };

  const handleToggleActive = async (row) => {
    const active = !row.active;
    setTogglingIds((prev) => new Set(prev).add(row.id));
    replaceRow({ ...row, active });
    try {
      const { ok, data } = await patchRecord(row.id, { active });
      if (!ok) throw new Error(JSON.stringify(data));
      replaceRow(data);
      setToast({
        severity: "success",
        message: `${row.name} is now ${active ? "active" : "inactive"}${
          active ? "" : " — automated EMI reminders are paused"
        }.`,
      });
    } catch (error) {
      console.error("Error updating EMI active status:", error);
      replaceRow(row);
      setToast({ severity: "error", message: "Could not update the Active status. Please try again." });
    } finally {
      setTogglingIds((prev) => {
        const next = new Set(prev);
        next.delete(row.id);
        return next;
      });
    }
  };

  const handleSave = async (form) => {
    setSaving(true);
    try {
      const { ok, data } = await patchRecord(editRecord.id, form);
      if (!ok) return data?.detail ? { error: data.detail } : { errors: data };
      replaceRow(data);
      setEditRecord(null);
      setToast({ severity: "success", message: "Customer updated." });
      // Re-query so the row lands where the current filters/order put it.
      fetchRows();
      return null;
    } catch (error) {
      console.error("Error saving EMI customer:", error);
      return { error: "Could not save the customer. Please try again." };
    } finally {
      setSaving(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await fetch(`${EMI_REMINDERS_EXPORT_ENDPOINT}?${filterParams()}`, {
        headers: await buildHeaders(),
      });
      if (!response.ok) throw new Error("Export failed");
      const blob = await response.blob();
      const match = /filename="([^"]+)"/.exec(response.headers.get("Content-Disposition") || "");
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = match ? match[1] : `emi_reminders_${month}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting EMI records:", error);
      setToast({ severity: "error", message: "Could not export EMI records. Please try again." });
    } finally {
      setExporting(false);
    }
  };

  const remindersHeader = scheduleLabels.length
    ? `Reminders (${scheduleLabels[0]} → ${scheduleLabels[scheduleLabels.length - 1]})`
    : "Reminders";

  const columns = [
    {
      id: "name",
      label: "Customer / Loan A/c",
      minWidth: 180,
      render: (row) => (
        <Box>
          <Typography sx={{ ...typography.bodyLarge, fontWeight: 600 }}>{row.name}</Typography>
          <Typography sx={{ ...typography.bodySmall, color: grayFontColor }}>
            {row.loan_account_number || "—"}
          </Typography>
        </Box>
      ),
    },
    {
      id: "phone_number",
      label: "Phone",
      minWidth: 150,
      render: (row) => (
        <Typography sx={{ ...typography.bodyLarge, whiteSpace: "nowrap" }}>
          {formatPhone(row.country_code, row.phone_number)}
        </Typography>
      ),
    },
    {
      id: "payment_amount",
      label: "EMI amount",
      align: "right",
      render: (row) => (
        <Typography sx={{ ...typography.bodyLarge, fontWeight: 600, whiteSpace: "nowrap" }}>
          {formatAmount(row.payment_amount)}
        </Typography>
      ),
    },
    {
      id: "due_date",
      label: "Due date",
      render: (row) => (
        <Typography sx={{ ...typography.bodyLarge, fontWeight: 600, whiteSpace: "nowrap" }}>
          {formatDay(row.progress?.cycle_due_date)}
        </Typography>
      ),
    },
    {
      id: "next_due_date",
      label: "Next due date",
      render: (row) => (
        <Typography sx={{ ...typography.bodyLarge, whiteSpace: "nowrap" }}>
          {formatDay(row.progress?.next_due_date)}
        </Typography>
      ),
    },
    {
      id: "progress",
      label: remindersHeader,
      minWidth: 220,
      render: (row) => <ReminderProgress progress={row.progress} />,
    },
    {
      id: "last_sent",
      label: "Last sent",
      render: (row) => (
        <Typography sx={{ ...typography.bodyLarge, whiteSpace: "nowrap" }}>
          {row.progress?.last_sent ? formatInTenantTz(row.progress.last_sent, "D MMM, hh:mm A") : "-"}
        </Typography>
      ),
    },
    {
      id: "active",
      label: "Active",
      render: (row) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Switch
            checked={!!row.active}
            onChange={() => handleToggleActive(row)}
            disabled={togglingIds.has(row.id)}
            size="small"
            sx={ACTIVE_SWITCH_SX}
            inputProps={{ "aria-label": `${row.name} active` }}
          />
          <Typography sx={{ ...typography.bodySmall, color: row.active ? "#1E7A4C" : grayFontColor }}>
            {row.active ? "Active" : "Inactive"}
          </Typography>
        </Box>
      ),
    },
    {
      id: "actions",
      label: "Actions",
      align: "center",
      render: (row) => (
        <IconButton
          size="small"
          onClick={() => setEditRecord(row)}
          aria-label={`Edit ${row.name}`}
          sx={{ border: "1px solid #DCDFE5", borderRadius: 1.5 }}
        >
          <EditOutlined fontSize="small" />
        </IconButton>
      ),
    },
  ];

  const from = rowCount === 0 ? 0 : controller.page * controller.rowsPerPage + 1;
  const to = Math.min(rowCount, controller.page * controller.rowsPerPage + rows.length);
  const showingText =
    rowCount <= controller.rowsPerPage
      ? `Showing ${rows.length} of ${rowCount} customers`
      : `Showing ${from}–${to} of ${rowCount} customers`;

  return (
    <Box sx={{ bgcolor: "#FFFFFF", border: "1px solid #E1E8F2", borderRadius: 3, p: 2.5 }}>
      <Box sx={{ display: "flex", alignItems: "flex-end", gap: 2, mb: 2, flexWrap: "wrap" }}>
        <Box sx={{ flex: "1 1 320px", maxWidth: 420 }}>
          <Typography component="span" sx={FILTER_LABEL_SX}>
            Search
          </Typography>
          <TableSearchInput
            searchQuery={searchQuery}
            onSearchChange={handleSearchChange}
            placeholder="Customer name, loan A/c no. or phone"
          />
        </Box>
        <Box>
          <Typography component="span" sx={FILTER_LABEL_SX}>
            Due date
          </Typography>
          <DueDayFilter value={dueDay} options={dueDays} onChange={handleDueDayChange} />
        </Box>
        <Box sx={{ ml: "auto" }}>
          <ButtonOne variant="outlined" onClick={handleExport} disabled={exporting || rowCount === 0}>
            {exporting ? "Exporting…" : "Export CSV"}
          </ButtonOne>
        </Box>
      </Box>

      <AppTable
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        loading={loading}
        emptyMessage={
          searchQuery.trim() || dueDay ? "No customers match your search or filter" : "No EMI customers yet"
        }
        maxHeight={620}
        hideMainScroll={false}
        containerSx={{ overflowX: "auto" }}
        headCellSx={{
          bgcolor: ColorsConstants.primaryBackgroundColor,
          color: "#FFFFFF",
          fontWeight: 600,
          borderBottom: "none",
        }}
        count={rowCount}
        page={controller.page}
        rowsPerPage={controller.rowsPerPage}
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        onPageChange={(_, newPage) => setController((prev) => ({ ...prev, page: newPage }))}
        onRowsPerPageChange={(e) => setController({ rowsPerPage: parseInt(e.target.value, 10), page: 0 })}
      />

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2, gap: 2, flexWrap: "wrap" }}>
        <ReminderProgressLegend />
        <Typography sx={{ ...typography.bodySmall, color: grayFontColor }}>{showingText}</Typography>
      </Box>

      <EMIRecordFormDialog
        open={!!editRecord}
        record={editRecord}
        onClose={() => setEditRecord(null)}
        onSave={handleSave}
        saving={saving}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)} variant="filled">
            {toast.message}
          </Alert>
        ) : (
          <span />
        )}
      </Snackbar>
    </Box>
  );
};

export default EMIRecordsTab;
